import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { useAuth } from '../auth';
import { CompactTable } from '../components/CompactTable';
import {
  createPortalTelephone,
  getPortalTelephonePassword,
  getPortalTelephones,
  PortalApiError,
  updatePortalTelephone,
} from '../services/portalApi';
import type {
  PortalTelephone,
  PortalTelephoneCreateInput,
  PortalTelephoneUpdateInput,
} from '../types/api';

type TelephoneSortKey = 'email' | 'phoneNumber' | 'status';
type TelephoneSortDirection = 'ascending' | 'descending';

interface TelephoneSort {
  key: TelephoneSortKey;
  direction: TelephoneSortDirection;
}

interface TelephoneForm {
  phoneNumber: string;
  email: string;
  password: string;
  isBlocked: boolean;
  blockedReason: string;
}

const emptyForm: TelephoneForm = {
  phoneNumber: '',
  email: '',
  password: '',
  isBlocked: false,
  blockedReason: '',
};

export function TelephonesPage() {
  const { getAccessToken } = useAuth();
  const [telephones, setTelephones] = useState<PortalTelephone[]>([]);
  const [sort, setSort] = useState<TelephoneSort | null>(null);
  const [form, setForm] = useState<TelephoneForm>(emptyForm);
  const [editingNumber, setEditingNumber] = useState<string | null>(null);
  const [revealedPasswords, setRevealedPasswords] = useState<Record<string, string>>({});
  const [loadingPassword, setLoadingPassword] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(true);

  const sortedTelephones = useMemo(() => {
    if (!sort) return telephones;

    const direction = sort.direction === 'ascending' ? 1 : -1;
    return [...telephones].sort((first, second) => {
      const firstValue = getTelephoneSortValue(first, sort.key);
      const secondValue = getTelephoneSortValue(second, sort.key);
      return firstValue.localeCompare(secondValue, undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * direction;
    });
  }, [sort, telephones]);

  const loadTelephones = useCallback(async (): Promise<void> => {
    setIsBusy(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      const response = await getPortalTelephones(token);
      setTelephones(response.telephones);
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsBusy(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    let cancelled = false;

    async function loadInitialTelephones(): Promise<void> {
      try {
        const token = await getAccessToken();
        const response = await getPortalTelephones(token);
        if (!cancelled) setTelephones(response.telephones);
      } catch (error) {
        if (!cancelled) setMessage(toMessage(error));
      } finally {
        if (!cancelled) setIsBusy(false);
      }
    }

    void loadInitialTelephones();
    return () => { cancelled = true; };
  }, [getAccessToken]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setMessage(null);
    if (form.isBlocked && !form.blockedReason.trim()) {
      setMessage('Enter a reason when blocking a telephone.');
      return;
    }

    setIsBusy(true);
    try {
      const token = await getAccessToken();
      if (editingNumber) {
        const input: PortalTelephoneUpdateInput = {
          email: form.email,
          password: form.password,
          isBlocked: form.isBlocked,
          blockedReason: form.isBlocked ? form.blockedReason : '',
        };
        await updatePortalTelephone(token, editingNumber, input);
      } else {
        const input: PortalTelephoneCreateInput = {
          phoneNumber: form.phoneNumber,
          email: form.email,
          password: form.password,
        };
        await createPortalTelephone(token, input);
      }
      setEditingNumber(null);
      setForm(emptyForm);
      setRevealedPasswords({});
      await loadTelephones();
    } catch (error) {
      setMessage(toMessage(error));
      setIsBusy(false);
    }
  }

  async function togglePassword(phoneNumber: string): Promise<void> {
    if (revealedPasswords[phoneNumber]) {
      setRevealedPasswords((current) => {
        const next = { ...current };
        delete next[phoneNumber];
        return next;
      });
      return;
    }

    setLoadingPassword(phoneNumber);
    setMessage(null);
    try {
      const token = await getAccessToken();
      const response = await getPortalTelephonePassword(token, phoneNumber);
      setRevealedPasswords((current) => ({
        ...current,
        [phoneNumber]: response.password,
      }));
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setLoadingPassword(null);
    }
  }

  function changeSort(key: TelephoneSortKey): void {
    setSort((current) => {
      if (!current || current.key !== key) {
        return { key, direction: 'ascending' };
      }
      if (current.direction === 'ascending') {
        return { key, direction: 'descending' };
      }
      return null;
    });
  }

  function sortDirection(key: TelephoneSortKey): TelephoneSortDirection | undefined {
    return sort?.key === key ? sort.direction : undefined;
  }

  function beginEdit(telephone: PortalTelephone): void {
    setEditingNumber(telephone.PhoneNumber);
    setForm({
      phoneNumber: telephone.PhoneNumber,
      email: telephone.Email,
      password: '',
      isBlocked: telephone.IsBlocked,
      blockedReason: telephone.BlockedReason ?? '',
    });
    setMessage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function cancelEdit(): void {
    setEditingNumber(null);
    setForm(emptyForm);
    setMessage(null);
  }

  return (
    <section className="events-page">
      <Link className="back-link" to="/admin">← Back to administration</Link>
      <div className="section-heading">
        <div>
          <p className="eyebrow">Administration</p>
          <h1>Telephone resources</h1>
          <p className="hero-description">
            A telephone is available unless it has been blocked or booked.
          </p>
        </div>
      </div>

      <form className="event-form" onSubmit={(event) => void handleSubmit(event)}>
        <h2>{editingNumber ? 'Edit telephone' : 'New telephone'}</h2>
        <div className="event-form-dates">
          <label>
            Telephone number
            <input required disabled={editingNumber !== null} placeholder="+4712345678"
              value={form.phoneNumber}
              onChange={(event) => setForm({ ...form, phoneNumber: event.target.value })} />
          </label>
          <label>
            Phonero email
            <input required type="email" value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })} />
          </label>
        </div>

        <label>
          {editingNumber ? 'New password (leave empty to keep current)' : 'Password'}
          <input required={!editingNumber} type="password" autoComplete="new-password"
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })} />
        </label>
        <p className="event-form-hint">
          Passwords are stored in Azure Key Vault and can be revealed from the list below.
        </p>

        {editingNumber ? (
          <>
            <label className="checkbox-label">
              <input type="checkbox" checked={form.isBlocked}
                onChange={(event) => setForm({
                  ...form,
                  isBlocked: event.target.checked,
                  blockedReason: event.target.checked ? form.blockedReason : '',
                })} />
              Block this telephone
            </label>
            {form.isBlocked ? (
              <label>
                Reason for blocking
                <input required maxLength={250} value={form.blockedReason}
                  onChange={(event) => setForm({ ...form, blockedReason: event.target.value })} />
              </label>
            ) : null}
          </>
        ) : null}

        <div className="event-form-actions">
          <button className="primary-button" disabled={isBusy} type="submit">
            {editingNumber ? 'Save changes' : 'Register telephone'}
          </button>
          {editingNumber ? (
            <button className="secondary-button" type="button" onClick={cancelEdit}>Cancel</button>
          ) : null}
        </div>
      </form>

      {message ? <p className="api-message" role="alert">{message}</p> : null}

      <div className="telephone-list-section">
        {isBusy && telephones.length === 0 ? <p>Loading telephones...</p> : null}
        {!isBusy && !message && telephones.length === 0 ? (
          <div className="event-empty-state">
            <h2>No telephones registered</h2>
            <p>Register the first telephone using the form above.</p>
          </div>
        ) : null}
        {telephones.length > 0 ? (
          <CompactTable
            label="Registered telephone resources"
            headers={[
              {
                key: 'email',
                label: 'Email',
                onSort: () => changeSort('email'),
                sortDirection: sortDirection('email'),
              },
              {
                key: 'phoneNumber',
                label: 'Phone number',
                onSort: () => changeSort('phoneNumber'),
                sortDirection: sortDirection('phoneNumber'),
              },
              { key: 'password', label: 'Password' },
              {
                key: 'status',
                label: 'Status',
                onSort: () => changeSort('status'),
                sortDirection: sortDirection('status'),
              },
              { key: 'actions', label: '' },
            ]}
          >
            {sortedTelephones.map((telephone) => {
              const password = revealedPasswords[telephone.PhoneNumber];
              const isLoading = loadingPassword === telephone.PhoneNumber;
              return (
                <tr key={telephone.PhoneNumber}>
                  <td>{telephone.Email}</td>
                  <td className="compact-table-mono">{telephone.PhoneNumber}</td>
                  <td>
                    <div className="password-cell">
                      <span className={password ? 'compact-table-mono' : 'password-hidden'}>
                        {password ?? '••••••••'}
                      </span>
                      <button className="icon-button password-toggle" type="button"
                        disabled={isLoading}
                        aria-label={password
                          ? `Hide password for ${telephone.PhoneNumber}`
                          : `Show password for ${telephone.PhoneNumber}`}
                        title={password ? 'Hide password' : 'Show password'}
                        onClick={() => void togglePassword(telephone.PhoneNumber)}>
                        <EyeIcon crossed={Boolean(password)} />
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className={telephone.IsBlocked
                      ? 'event-status telephone-status-blocked'
                      : telephone.ActiveBooking
                        ? 'event-status telephone-status-booked'
                        : 'event-status telephone-status-active'}>
                      {telephone.IsBlocked
                        ? 'Blocked'
                        : telephone.ActiveBooking
                          ? 'Booked'
                          : 'Available'}
                    </span>
                    {telephone.BlockedReason ? (
                      <small className="blocked-reason">{telephone.BlockedReason}</small>
                    ) : null}
                  </td>
                  <td className="compact-table-actions">
                    <button className="table-action-button" type="button"
                      onClick={() => beginEdit(telephone)}>Edit</button>
                  </td>
                </tr>
              );
            })}
          </CompactTable>
        ) : null}
      </div>
    </section>
  );
}

function getTelephoneSortValue(
  telephone: PortalTelephone,
  key: TelephoneSortKey,
): string {
  if (key === 'email') return telephone.Email;
  if (key === 'phoneNumber') return telephone.PhoneNumber;
  if (telephone.IsBlocked) return 'Blocked';
  return telephone.ActiveBooking ? 'Booked' : 'Available';
}

function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
      <circle cx="12" cy="12" r="2.5" />
      {crossed ? <path d="m4 4 16 16" /> : null}
    </svg>
  );
}

function toMessage(error: unknown): string {
  if (error instanceof PortalApiError) return error.message;
  return 'The telephone request could not be completed.';
}
